import { network } from "../config";
import {
  FeedbackAssignment,
  FeedbackResults,
  ChapterQuiz,
  NewChapterQuiz,
  NearestProjectQuizResponse,
  NewQuiz,
  Quiz,
  QuizAttempt,
  QuizAttemptAnswerPayload,
  QuizContextRefsPayload,
  QuizStatistics,
  TypeQuizAttemptFilters,
  TypeQuizFilters,
} from "../types/Quiz";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import url from "../utils/formApiUrl";
import customKy from "./kyExtend";

const { quiz } = network;

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "quizes/";

const getAuthHeader = (token: string) => {
  return { Authorization: `Bearer ${token}` };
};

export default class QuizApi {
  static async createQuiz(
    token: string,
    payload: { newQuiz: NewChapterQuiz },
  ): Promise<TypeDefaultResponse & { quiz?: ChapterQuiz }> {
    return await customKy
      .post(url(baseURL, quiz.createQuiz), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async updateQuiz(
    token: string,
    payload: {
      updateData: Partial<Quiz>;
    },
  ): Promise<TypeDefaultResponse & { quiz?: ChapterQuiz }> {
    return await customKy
      .patch(url(baseURL, quiz.updateQuiz, { id: payload.updateData._id }), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async deleteQuiz(
    token: string,
    quizId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(url(baseURL, quiz.deleteQuiz, { id: quizId }), {
        headers: getAuthHeader(token),
        json: { quizId },
      })
      .then((res) => res.json());
  }

  static async getQuizById(
    token: string,
    quizId: string,
  ): Promise<
    TypeDefaultResponse & {
      quiz?: Quiz;
    }
  > {
    return await customKy
      .get(url(baseURL, quiz.getQuizById, { id: quizId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getQuizes(
    token: string,
    payload: {
      limit?: number | string;
      page?: number | string;
      filters?: TypeQuizFilters;
    },
  ): Promise<
    TypeDefaultResponse & {
      quizes?: Quiz[];
      total?: number;
    }
  > {
    return await customKy
      .post(url(baseURL, quiz.getQuizes), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getQuizAttempts(
    token: string,
    payload: {
      quizId?: string;
      limit?: number | string;
      page?: number | string;
      filters?: TypeQuizAttemptFilters;
    },
  ): Promise<
    TypeDefaultResponse & {
      attempts?: QuizAttempt[];
      total?: number;
    }
  > {
    return await customKy
      .post(url(baseURL, quiz.getQuizAttempts), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async submitQuizAttempt(
    token: string,
    payload: {
      quizId: string;
      answers: QuizAttemptAnswerPayload[];
      project?: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      attempt?: QuizAttempt;
    }
  > {
    return await customKy
      .post(url(baseURL, quiz.submitQuizAttempt, { id: payload.quizId }), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async getNearestProjectQuiz(
    token: string,
    projectId: string,
  ): Promise<NearestProjectQuizResponse> {
    return await customKy
      .get(url(baseURL, quiz.getNearestProjectQuiz, { id: projectId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getQuizStatistics(
    token: string,
    quizId: string,
  ): Promise<TypeDefaultResponse & { statistics?: QuizStatistics }> {
    return await customKy
      .get(url(baseURL, quiz.getQuizStatistics, { id: quizId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async cloneQuiz(
    token: string,
    payload: {
      quizId: string;
      refs?: QuizContextRefsPayload;
      placement?: { bucketIndex: number; quizIndex: number };
      projectConfig?: {
        isConfidential?: boolean;
        isEditable?: boolean;
        isScoreShown?: boolean;
      };
      isStartingQuiz?: boolean;
    },
  ): Promise<TypeDefaultResponse & { quiz?: Quiz }> {
    return await customKy
      .post(url(baseURL, quiz.cloneQuiz, { id: payload.quizId }), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async moveProjectQuiz(
    token: string,
    payload: {
      projectId: string;
      quizId: string;
      placement: { bucketIndex: number; quizIndex: number };
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .post(
        url(baseURL, quiz.moveProjectQuiz, {
          id: payload.quizId,
          projectId: payload.projectId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async createFeedbackQuiz(
    token: string,
    payload: {
      projectId: string;
      subjectId: string;
      raterIds: string[];
      newQuiz: NewQuiz;
      traceId?: string;
      tenantId?: string;
    },
  ): Promise<TypeDefaultResponse & { quiz?: Quiz | null }> {
    return await customKy
      .post(url(baseURL, quiz.createFeedbackQuiz), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async cloneFeedbackQuiz(
    token: string,
    payload: {
      templateQuizId: string;
      projectId: string;
      subjectId: string;
      raterIds: string[];
      traceId?: string;
      tenantId?: string;
    },
  ): Promise<TypeDefaultResponse & { quiz?: Quiz | null }> {
    return await customKy
      .post(
        url(baseURL, quiz.cloneFeedbackQuiz, { id: payload.templateQuizId }),
        { headers: getAuthHeader(token), json: payload },
      )
      .then((res) => res.json());
  }

  static async getFeedbackAssignments(
    token: string,
    projectId?: string,
  ): Promise<TypeDefaultResponse & { assignments?: FeedbackAssignment[] }> {
    return await customKy
      .get(url(baseURL, quiz.getFeedbackAssignments), {
        headers: getAuthHeader(token),
        searchParams: projectId ? { projectId } : undefined,
      })
      .then((res) => res.json());
  }

  static async getFeedbackResults(
    token: string,
    quizId: string,
  ): Promise<TypeDefaultResponse & { results?: FeedbackResults }> {
    return await customKy
      .get(url(baseURL, quiz.getFeedbackResults, { id: quizId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async cancelFeedbackQuiz(
    token: string,
    quizId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(url(baseURL, quiz.cancelFeedbackQuiz, { id: quizId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }
}
